import { Sello } from './Sello';
import { NEGOCIO } from '@/lib/negocio';
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useAuthStore } from '@/store/auth.store';
import { useUiStore } from '@/store/ui.store';
import { gruposVisibles, grupoDeRuta, type GrupoMenu } from '@/lib/sidebar-grupos';
import { usePermisoHistorial } from '@/hooks/usePermisoHistorial';
import { useCrmView } from '@/hooks/useCrmView';
import { cn } from '@/lib/cn';

/**
 * A4 · El menú, agrupado por lo que la persona intenta hacer.
 *
 * Antes era una lista plana de 19 entradas ordenada por frecuencia de uso. Con
 * ese largo, encontrar algo es leer los 19 nombres.
 *
 * La estructura, los permisos y qué grupo corresponde a cada ruta viven en
 * `lib/sidebar-grupos` y están testeados aparte. Acá queda solo el pintado y el
 * plegado.
 *
 * Dos decisiones de comportamiento:
 *
 *  - **El grupo de la pantalla actual se abre solo**, siempre. Si alguien llega
 *    por un enlace directo a una pantalla de un grupo que tenía plegado, el
 *    menú tiene que mostrarle dónde está parado.
 *  - **Con el sidebar angosto no se pliega nada**: en 16px solo hay iconos, y un
 *    encabezado que no se lee no ayuda. Se muestran todos los iconos en fila,
 *    que es el comportamiento que ya tenía.
 */

const CLAVE_PLEGADOS = 'sidebar.gruposPlegados';

/** Los grupos plegados, recordados entre sesiones. */
function leerPlegados(): string[] {
  try {
    const crudo = localStorage.getItem(CLAVE_PLEGADOS);
    const v = crudo ? JSON.parse(crudo) : [];
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [];
  } catch {
    // Modo incógnito o storage bloqueado: nada plegado es un default sano.
    return [];
  }
}

export function Sidebar() {
  const role = useAuthStore((s) => s.user?.role);
  const sidebarOpen = useUiStore((s) => s.sidebarOpen);
  const setSidebar = useUiStore((s) => s.setSidebar);
  const location = useLocation();

  // Al cambiar de pantalla en el teléfono, el menú se cierra solo: si no, tapa
  // la pantalla a la que la persona acaba de entrar.
  useEffect(() => {
    if (window.matchMedia('(max-width: 767px)').matches) setSidebar(false);
  }, [location.pathname, setSidebar]);
  const { pathname } = useLocation();
  // El nombre del negocio de ESTA instalación, para que el panel diga de quién
  // es. Cacheado 5 min por `useCrmView`; `undefined` mientras carga.
  const crm = useCrmView();
  const negocio = crm?.businessName;
  // Qué módulos ve este negocio. Sin respuesta todavía, el menú sale completo.
  const modulos = crm?.osito?.modulos;

  // Lo que el rol no alcanza a decidir lo contesta la API. Ver sidebar-grupos.
  const historial = usePermisoHistorial();
  const grupos = gruposVisibles(role, { historialCopiloto: historial.puedeVer }, modulos);
  const grupoActual = grupoDeRuta(pathname);
  const [plegados, setPlegados] = useState<string[]>(leerPlegados);

  // El grupo de la pantalla actual nunca queda plegado.
  useEffect(() => {
    if (grupoActual) setPlegados((p) => p.filter((id) => id !== grupoActual));
  }, [grupoActual]);

  useEffect(() => {
    try {
      localStorage.setItem(CLAVE_PLEGADOS, JSON.stringify(plegados));
    } catch {
      // Si no se puede guardar, el menú sigue funcionando: solo no recuerda.
    }
  }, [plegados]);

  const alternar = (id: string) =>
    setPlegados((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <>
      {/* EN TELÉFONO el menú se abre ENCIMA, no al lado (23-sep-2026): con 256
          píxeles fijos, en un iPhone no quedaba ancho para el chat y los
          mensajes salían cortados por la derecha. Detrás, una capa oscura que
          se toca para cerrarlo. */}
      {sidebarOpen && (
        <button
          type="button"
          aria-label="Cerrar el menú"
          onClick={() => setSidebar(false)}
          className="mac-velo fixed inset-0 z-30 md:hidden"
        />
      )}
      <aside
        className={cn(
          // 21-oct, macOS 27: barra lateral opaca de 240 pegada al borde (#E6E6E6 / #161616), sin filo.
          'flex shrink-0 flex-col overflow-hidden bg-[var(--mac-lateral)] text-slate-900 transition-[width] duration-200 dark:text-slate-100 ' +
            'fixed inset-y-0 left-0 z-40 md:static md:z-auto',
          sidebarOpen ? 'w-60' : 'w-0 overflow-hidden md:w-14',
        )}
      >
      <div
        data-tour="logo"
        className="flex h-14 shrink-0 items-center gap-2.5 px-[14px] lateral:h-[52px]"
      >
        <Sello nombre={negocio || NEGOCIO} />
        {sidebarOpen && (
          <div className="min-w-0 leading-tight">
            {/*
              Arriba el NEGOCIO, abajo el producto. Al revés (que es como venía)
              el equipo de la tienda abre su panel y lee el nombre de la
              plataforma, no el suyo — y con varios clientes sobre el mismo
              producto, nada distingue un panel de otro.
              Cae a «Osito» mientras la config carga o si falla.
            */}
            {/* 13-oct: arriba solo el negocio; el sistema no se nombra. */}
            <span className="block truncate text-sm font-semibold text-slate-900 dark:text-slate-50">
              {negocio || NEGOCIO}
            </span>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto overscroll-contain px-2.5 pb-5 pt-1">
        {grupos.map((grupo) => (
          <Grupo
            key={grupo.id}
            grupo={grupo}
            expandido={sidebarOpen}
            plegado={sidebarOpen && plegados.includes(grupo.id)}
            onAlternar={() => alternar(grupo.id)}
          />
        ))}
      </nav>
      </aside>
    </>
  );
}

function Grupo({
  grupo,
  expandido,
  plegado,
  onAlternar,
}: {
  grupo: GrupoMenu;
  expandido: boolean;
  plegado: boolean;
  onAlternar: () => void;
}) {
  return (
    <div className={expandido ? 'pb-2' : undefined}>
      {/* Con el sidebar angosto no hay encabezado: solo caben los iconos. */}
      {expandido && (
        <button
          type="button"
          onClick={onAlternar}
          aria-expanded={!plegado}
          className="mb-0.5 mt-3 flex min-h-8 w-full items-center justify-between rounded-[6px] pl-[9px] pr-2 text-[11px] font-semibold leading-[14px] text-[var(--mac-lateral-rotulo)] transition-colors hover:bg-[var(--mac-btn)] lateral:min-h-6"
        >
          <span>{grupo.label}</span>
          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', plegado && '-rotate-90')} />
        </button>
      )}

      {!plegado &&
        grupo.items.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            data-tour={to}
            title={expandido ? undefined : label}
            className={({ isActive }) =>
              cn(
                // 21-oct, macOS 27: fila de 32 (44 en el teléfono), radio 8; lo elegido en gris, el ícono en el acento.
                'flex min-h-11 items-center gap-2 rounded-[8px] pl-[9px] pr-2 text-sm text-slate-900 dark:text-slate-100 lateral:min-h-8',
                isActive && 'bg-[var(--mac-lateral-sel)]',
              )
            }
          >
            <Icon className="h-[17px] w-[17px] shrink-0 text-acento-icono" strokeWidth={1.75} />
            {expandido && <span className="truncate">{label}</span>}
          </NavLink>
        ))}
    </div>
  );
}
